import { useState, useEffect, useMemo } from "react";
import { DataTablePageEvent } from "primereact/datatable";
import { Checkbox } from "primereact/checkbox";
import CustomButton from "../Common/CustomButton/CustomButton";
import { useToastStore } from "../../stores/toastStore";
import ScenarioGroupTable from "./ScenarioGroupTable";

type ScenarioPayload = {
	items: any[];
	total_items: number;
	page: number;
	page_size: number;
	scenario_name: string;
};

type AllocationChange = {
	tender_scenario_id: number;
	allocation_pct: number;
};

type Props = {
	products: ScenarioPayload | null;
	first: number;
	rows: number;
	onPage: (e: Pick<DataTablePageEvent, "first" | "rows">) => void;
	updateVolumeAllocation: (changes: AllocationChange[], isRecommended: boolean) => Promise<void>;
	currentIsRecommended: boolean;
	onDeleteScenario: () => Promise<void>;
};

export default function ScenarioTable({
	products,
	first,
	rows,
	onPage,
	updateVolumeAllocation,
	currentIsRecommended,
	onDeleteScenario,
}: Props) {
	// Track edits: Map<tender_scenario_id, new_allocation_pct>
	const [editedCells, setEditedCells] = useState<Map<number, number>>(new Map());
	// Track recently saved cells to show green feedback
	const [savedCells, setSavedCells] = useState<Set<number>>(new Set());
	const [isSaving, setIsSaving] = useState(false);
	// Force re-render when cancel is clicked
	const [renderKey, setRenderKey] = useState(0);
	// Track is_recommended checkbox state
	const [isRecommended, setIsRecommended] = useState(false);
	const { error: showError } = useToastStore();

	// Check if this is a default preloaded scenario that shouldn't be edited
	const isDefaultScenario = products?.scenario_name === "As Is Scenario" || products?.scenario_name === "Cherry Picking";

	// Reset edits when products change (new page/scenario)
	useEffect(() => {
		setEditedCells(new Map());
		setSavedCells(new Set());
		setRenderKey(prev => prev + 1);
		// Initialize is_recommended from prop
		setIsRecommended(currentIsRecommended);
	}, [products, currentIsRecommended]);

	// Handle edit directly from editor callback
	const handleCellEdit = (tender_scenario_id: number, newValue: string, originalValue: number) => {
		const newAllocation = Number(newValue);

		// Only track if value actually changed
		if (!isNaN(newAllocation) && newAllocation !== originalValue) {
			setEditedCells((prev) => {
				const updated = new Map(prev);
				updated.set(tender_scenario_id, newAllocation);
				return updated;
			});
		} else if (newAllocation === originalValue) {
			// Remove from edited if reverted to original
			setEditedCells((prev) => {
				const updated = new Map(prev);
				updated.delete(tender_scenario_id);
				return updated;
			});
		}
	};

	const handleSave = async () => {
		if (editedCells.size === 0 && isRecommended === currentIsRecommended) {
			// Nothing to save if no edits and is_recommended hasn't changed
			return;
		}

		// Check for validation errors
		const hasValidationErrors = products?.items?.some((item: any) => isAllocationInvalid(item));
		if (hasValidationErrors) {
			showError("Validation Error", "Allocations must sum to 100% for each country and product combination.");
			return;
		}

		setIsSaving(true);
		try {
			const changes: AllocationChange[] = Array.from(editedCells.entries()).map(
				([tender_scenario_id, allocation_pct]) => ({
					tender_scenario_id,
					allocation_pct,
				})
			);

			await updateVolumeAllocation(changes, isRecommended);

			// Mark cells as saved and show green feedback
			const savedIds = new Set(editedCells.keys());
			setSavedCells(savedIds);
			setEditedCells(new Map()); // Clear edits on success

			// Clear saved status after 3 seconds
			setTimeout(() => {
				setSavedCells(new Set());
			}, 3000);
		} catch (error) {
			// Error handling done in parent
			console.error("Save failed:", error);
		} finally {
			setIsSaving(false);
		}
	};

	const handleCancel = () => {
		setEditedCells(new Map());
		// Reset is_recommended to original value
		setIsRecommended(currentIsRecommended);
		// Force re-render to show original values
		setRenderKey(prev => prev + 1);
	};

	const hasEdits = editedCells.size > 0 || isRecommended !== currentIsRecommended;

	const formatCurrency = (value: number | null | undefined): string => {
		if (value == null || isNaN(value)) {
			return '-';
		}
		if (value >= 1000000) {
			return `${(value / 1000000).toFixed(4)}M (€)`;
		} else if (value >= 1000) {
			return `${(value / 1000).toFixed(4)}K (€)`;
		}
		return `${value.toFixed(4)} (€)`;
	};

	const formatVolume = (value: number | null | undefined): string => {
		if (value == null || isNaN(value)) {
			return '-';
		}
		if (value >= 1000000) {
			return `${(value / 1000000).toFixed(2)}M`;
		} else if (value >= 1000) {
			return `${(value / 1000).toFixed(2)}K`;
		}
		return value.toFixed(2);
	};

	const formatPercentage = (value: number | null | undefined): string => {
		if (value == null || isNaN(value)) {
			return '-';
		}
		return `${value.toFixed(2)}%`;
	};

	// Get display value for allocation (edited or original)
	const getAllocationValue = (rowData: any) => {
		if (editedCells.has(rowData.tender_scenario_id)) {
			const editedValue = editedCells.get(rowData.tender_scenario_id);
			return editedValue ?? 0;
		}
		return rowData.allocation_pct ?? 0;
	};

	// Check if a cell has been edited
	const isCellEdited = (rowData: any) => {
		return editedCells.has(rowData.tender_scenario_id);
	};

	// Check if a cell was recently saved
	const isCellSaved = (rowData: any) => {
		return savedCells.has(rowData.tender_scenario_id);
	};

	// Group items by country and article
	const groupedData = useMemo(() => {
		if (!products?.items) return [];

		const groups = new Map<string, any[]>();

		products.items.forEach((item: any) => {
			const key = `${item.country}|${item.article}`;
			if (!groups.has(key)) {
				groups.set(key, []);
			}
			groups.get(key)!.push(item);
		});

		return Array.from(groups.entries()).map(([key, items]) => {
			const [country, article] = key.split('|');
			return { country, article, items };
		});
	}, [products?.items]);

	// Calculate allocation sum for a country/article combination
	const isAllocationInvalid = (rowData: any) => {
		const country = rowData.country;
		const article = rowData.article;

		// Get all rows with same country and article
		const relatedRows = products?.items?.filter(
			(item: any) => item.country === country && item.article === article
		) || [];

		// Calculate total allocation considering edits
		const totalAllocation = relatedRows.reduce((sum: number, item: any) => {
			const allocation = editedCells.has(item.tender_scenario_id)
				? editedCells.get(item.tender_scenario_id)!
				: item.allocation_pct;
			return sum + (allocation || 0);
		}, 0);

		// Check if total is not 100% (with small tolerance for floating point)
		return Math.abs(totalAllocation - 100) > 0.01;
	};

	return (
		<div className="table-wrapper" style={{ width: "100%" }} key={renderKey}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
				<h3 className="title" style={{ margin: 0 }}>Detailed View</h3>
				{!isDefaultScenario && (
					<div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
							<Checkbox
								inputId="is_recommended"
								checked={isRecommended}
								onChange={(e) => setIsRecommended(e.checked ?? false)}
								disabled={isSaving}
								pt={{
									box: {
										style: {
											borderColor: 'var(--color-blue-500)',
											backgroundColor: isRecommended ? 'var(--color-blue-500)' : 'transparent'
										}
									}
								}}
							/>
							<label htmlFor="is_recommended" style={{ fontSize: "0.95rem", cursor: "pointer", userSelect: "none" }}>
								Mark as Recommended
							</label>
						</div>
						{hasEdits && (
							<span style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}>
								{editedCells.size} unsaved {editedCells.size === 1 ? "change" : "changes"}
							</span>
						)}
						<CustomButton
							iconsOnly={false}
							label="Delete"
							onClick={onDeleteScenario}
							disabled={isSaving}
							variant="secondary"
						/>
						<CustomButton
							iconsOnly={false}
							label="Cancel"
							onClick={handleCancel}
							disabled={!hasEdits || isSaving}
							variant="secondary"
						/>
						<CustomButton
							iconsOnly={false}
							label="Save Changes"
							onClick={handleSave}
							disabled={!hasEdits || isSaving}
						/>
					</div>
				)}
			</div>
			{/* Render grouped tables */}
			<div style={{
				maxHeight: "calc(100vh - 350px)",
				minHeight: "500px",
				overflowY: "auto",
				paddingRight: "0.5rem"
			}}>
				{groupedData.length > 0 ? (
					groupedData.map((group, index) => (
						<ScenarioGroupTable
							key={`${group.country}-${group.article}-${index}`}
							group={group}
							isDefaultScenario={isDefaultScenario}
							editedCells={editedCells}
							onCellEdit={handleCellEdit}
							getAllocationValue={getAllocationValue}
							isCellEdited={isCellEdited}
							isCellSaved={isCellSaved}
							isAllocationInvalid={isAllocationInvalid}
							formatCurrency={formatCurrency}
							formatVolume={formatVolume}
							formatPercentage={formatPercentage}
						/>
					))
				) : (
					<div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>
						No data available for this scenario
					</div>
				)}
			</div>

			{/* Pagination info */}
			{products && products.total_items > 0 && (
				<div style={{
					marginTop: "0.75rem",
					padding: "0.5rem 0.75rem",
					backgroundColor: "var(--surface-background)",
					borderRadius: "4px",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					fontSize: "0.85rem"
				}}>
					<div style={{ color: "var(--text-muted)" }}>
						Showing {groupedData.reduce((sum, g) => sum + g.items.length, 0)} rows across {groupedData.length} group{groupedData.length !== 1 ? 's' : ''}
						{products.total_items > rows && ` (${products.total_items} total rows)`}
					</div>
					{products.total_items > rows && (
						<div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
							<button
								onClick={() => onPage({ first: Math.max(0, first - rows), rows })}
								disabled={first === 0}
								style={{
									padding: "0.4rem 0.8rem",
									border: "1px solid var(--border-muted)",
									borderRadius: "4px",
									backgroundColor: first === 0 ? "var(--surface-variant)" : "var(--surface-default)",
									cursor: first === 0 ? "not-allowed" : "pointer",
									fontSize: "0.85rem"
								}}
							>
								Previous
							</button>
							<span style={{ padding: "0 0.5rem", fontSize: "0.85rem", color: "var(--text-muted)" }}>
								Page {Math.floor(first / rows) + 1} of {Math.ceil(products.total_items / rows)}
							</span>
							<button
								onClick={() => onPage({ first: first + rows, rows })}
								disabled={first + rows >= products.total_items}
								style={{
									padding: "0.4rem 0.8rem",
									border: "1px solid var(--border-muted)",
									borderRadius: "4px",
									backgroundColor: first + rows >= products.total_items ? "var(--surface-variant)" : "var(--surface-default)",
									cursor: first + rows >= products.total_items ? "not-allowed" : "pointer",
									fontSize: "0.85rem"
								}}
							>
								Next
							</button>
							<select
								value={rows}
								onChange={(e) => onPage({ first: 0, rows: Number(e.target.value) })}
								style={{
									padding: "0.4rem",
									border: "1px solid var(--border-muted)",
									borderRadius: "4px",
									fontSize: "0.85rem",
									marginLeft: "0.5rem"
								}}
							>
								<option value={100}>100 rows</option>
								<option value={200}>200 rows</option>
								<option value={300}>300 rows</option>
							</select>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
